import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight,
  Sparkles,
  History
} from 'lucide-react';
import { 
  VoyageIcon, 
  ChronoIcon, 
  LighthouseIcon, 
  PlanIcon 
} from '@/components/icons/CustomIcons';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout';
import { Card, CardHeader, CardContent, Button } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';
import { staggerContainer, staggerItem } from '@/lib/motion';
import { cn } from '@/lib/cn';
import { sessionApi, imageApi } from '@/lib/api';
import { MarketIndices, ShippingNews, RecentActivity } from '@/components/dashboard';

// Custom DryVox Logo
const DryVoxLogo = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const sizeClasses = { sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-7 h-7' };
  return (
    <svg viewBox="0 0 32 32" fill="none" className={sizeClasses[size]}>
      <circle cx="16" cy="6" r="4" stroke="currentColor" strokeWidth="2.5" fill="none"/>
      <path d="M16 10V26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M10 16H22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M8 28C8 22 12 20 16 26C20 20 24 22 24 28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M4 14C5.5 12 5.5 10 4 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
      <path d="M28 14C26.5 12 26.5 10 28 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    </svg>
  );
};

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
}

function StatCard({ icon: Icon, label, value, change, changeType = 'neutral' }: StatCardProps) {
  const changeColors = {
    positive: 'text-signal-success',
    negative: 'text-signal-error',
    neutral: 'text-gray-400',
  };

  return (
    <Card variant="glass" hover glow="cyan">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-400">{label}</p>
          <p className="text-2xl font-display font-bold text-white mt-1">{value}</p>
          {change && (
            <p className={cn('text-xs mt-1', changeColors[changeType])}>
              {change}
            </p>
          )}
        </div>
        <div className="p-3 rounded-xl bg-ocean-cyan/10 border border-ocean-cyan/20">
          <Icon className="w-5 h-5 text-ocean-cyan" />
        </div>
      </div>
    </Card>
  );
}

interface DashboardStats {
  totalSessions: number;
  sessionsToday: number;
  totalMinutes: number;
  imagesGenerated: number;
  isLoading: boolean;
}

/**
 * Dashboard Page
 * 
 * Features:
 * - Welcome message with user info
 * - Quick stats overview
 * - Quick actions
 * - Recent sessions
 */
export function Dashboard() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats>({
    totalSessions: 0,
    sessionsToday: 0,
    totalMinutes: 0,
    imagesGenerated: 0,
    isLoading: true,
  });
  
  const userName = user?.name || user?.email?.split('@')[0] || 'Captain';
  const greeting = getGreeting();

  function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }

  // Fetch stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [sessionsRes, imagesRes] = await Promise.all([
          sessionApi.list(0, 100).catch(() => ({ data: [] })),
          imageApi.history(0, 100).catch(() => ({ data: [] })),
        ]);

        const sessions = sessionsRes.data;
        const images = imagesRes.data;
        
        // Calculate sessions today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const sessionsToday = sessions.filter(s => 
          new Date(s.started_at) >= today
        ).length;
        
        // Calculate total minutes from session durations
        const totalSeconds = sessions.reduce((sum, s) => 
          sum + (s.duration_seconds || 0), 0
        );
        const totalMinutes = Math.round(totalSeconds / 60);

        setStats({
          totalSessions: sessions.length,
          sessionsToday,
          totalMinutes,
          imagesGenerated: images.length,
          isLoading: false,
        });
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
        setStats(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchStats();
  }, []);

  return (
    <AppShell>
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="space-y-8"
      >
        {/* Welcome Section */}
        <motion.div variants={staggerItem} className="mb-8">
          <div className="flex items-center gap-2 text-ocean-cyan text-sm font-medium mb-2">
            <Sparkles className="w-4 h-4" />
            <span>Welcome to DryVox</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-white">
            {greeting}, <span className="text-gradient">{userName}</span>
          </h1>
          <p className="text-gray-400 mt-2">
            Your AI-powered voice assistant for dry bulk shipping is ready to help.
          </p>
        </motion.div>

        {/* Quick Stats */}
        <motion.div variants={staggerItem}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={VoyageIcon}
              label="Sessions Today"
              value={stats.isLoading ? '...' : stats.sessionsToday}
              change={stats.sessionsToday > 0 ? `${stats.totalSessions} total` : 'Start your first session'}
              changeType={stats.sessionsToday > 0 ? 'positive' : 'neutral'}
            />
            <StatCard
              icon={ChronoIcon}
              label="Total Time"
              value={stats.isLoading ? '...' : `${stats.totalMinutes} min`}
              change={stats.totalMinutes > 0 ? 'Voice sessions' : 'Begin tracking'}
              changeType={stats.totalMinutes > 0 ? 'positive' : 'neutral'}
            />
            <StatCard
              icon={LighthouseIcon}
              label="Images Generated"
              value={stats.isLoading ? '...' : stats.imagesGenerated}
              change={stats.imagesGenerated > 0 ? 'AI generated' : 'Try image generation'}
              changeType={stats.imagesGenerated > 0 ? 'positive' : 'neutral'}
            />
            <StatCard
              icon={PlanIcon}
              label="Plan"
              value={user?.tier || 'Basic'}
              change="Unlimited sessions"
              changeType="neutral"
            />
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div variants={staggerItem}>
          <Card variant="glass" padding="lg">
            <CardHeader 
              title="Quick Actions" 
              subtitle="Start interacting with DryVox"
            />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Start Voice Session */}
                <Link to="/assistant">
                  <div className="group relative p-6 rounded-2xl bg-gradient-to-br from-ocean-cyan/10 to-ocean-blue/10 border border-ocean-cyan/20 hover:border-ocean-cyan/40 transition-all cursor-pointer">
                    <div className="flex items-start gap-4">
                      <div className="p-4 rounded-2xl bg-gradient-to-br from-ocean-cyan to-ocean-blue shadow-glow-cyan text-white">
                        <DryVoxLogo size="md" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white group-hover:text-ocean-cyan transition-colors">
                          Start Voice Session
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">
                          Talk to DryVox about market rates, calculations, or get mentoring
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-ocean-cyan group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </Link>

                {/* View History */}
                <Link to="/sessions">
                  <div className="group relative p-6 rounded-2xl bg-gradient-to-br from-ocean-purple/10 to-ocean-pink/10 border border-ocean-purple/20 hover:border-ocean-purple/40 transition-all cursor-pointer">
                    <div className="flex items-start gap-4">
                      <div className="p-4 rounded-2xl bg-gradient-to-br from-ocean-purple to-ocean-pink shadow-glow-purple">
                        <History className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white group-hover:text-ocean-purple transition-colors">
                          Session History
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">
                          Review past conversations and exported transcripts
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-ocean-purple group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Market Data & News Row */}
        <motion.div variants={staggerItem}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Market Indices */}
            <MarketIndices />
            
            {/* Shipping News */}
            <ShippingNews />
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div variants={staggerItem}>
          <RecentActivity />
        </motion.div>
      </motion.div>
    </AppShell>
  );
}

export default Dashboard;

